import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  FormSubentityTypeEnum,
  FormInterface,
  FormSubentityInterface,
} from 'src/shared/types/entities/form-interface'
import { getFormSubentitiesSettingsIcons } from '../../../utils/get-form-subentities-settings-icons'
import { CheckSquareIcon } from 'src/editor/shared/icons/check-square-icon'
import { getContactSlugName } from 'src/editor/shared/utils/get-contact-field-title'
import { FormAddNewSubentitySettingsItem } from './form-add-new-subentity-settings-item'
import { useUpdateEntity } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { ContactSlugEnum } from 'src/shared/enums/contact-fields-enum'
import {
  createFormCheckboxSubentity,
  createFormFieldSubentity,
} from '../../../create-form'
import {
  formAddNewSubentitySettingsList,
  HORIZONTAL_LINE_INDEXES,
} from './constants'
import { Toggle } from 'src/editor/shared/ui-lib/toggle'
import { FormSubentityNumberInput } from './form-subentity-number-input'

interface FormAddNewSubentitySettingsProps {
  entity: FormInterface
}

const HorizontalLine = () => <hr className="my-2 text-divider00" />

export const FormAddNewSubentitySettings = ({
  entity,
}: FormAddNewSubentitySettingsProps) => {
  const { t } = useTranslation()

  const updateEntity = useUpdateEntity<FormInterface>()

  const getIsExisted = (slug: ContactSlugEnum) => {
    return entity.subEntities.some(
      subentity =>
        subentity.type === FormSubentityTypeEnum.Field &&
        subentity.slug === slug,
    )
  }

  const onToggle = (slug: ContactSlugEnum) => {
    const isExisted = getIsExisted(slug)
    if (isExisted) {
      updateEntity({
        ...entity,
        subEntities: entity.subEntities.filter(
          subentity =>
            subentity.type !== FormSubentityTypeEnum.Field ||
            subentity.slug !== slug,
        ),
      })
    } else {
      updateEntity({
        ...entity,
        subEntities: entity.subEntities.reduce((acc, subentity, index) => {
          // NOTE: Add the new field before the submit button
          if (index === entity.subEntities.length - 1) {
            acc.push(createFormFieldSubentity({ slug, t }))
          }
          acc.push(subentity)
          return acc
        }, [] as FormSubentityInterface[]),
      })
    }
  }

  const checkboxes = entity.subEntities.filter(
    subentity => subentity.type === FormSubentityTypeEnum.Checkbox,
  )

  const onChangeNumberOfCheckboxes = (value: number) => {
    if (value > checkboxes.length) {
      updateEntity({
        ...entity,
        subEntities: entity.subEntities.reduce((acc, subentity, index) => {
          // NOTE: Add the new checkbox before the submit button
          if (index === entity.subEntities.length - 1) {
            acc.push(createFormCheckboxSubentity({ t }))
          }
          acc.push(subentity)
          return acc
        }, [] as FormSubentityInterface[]),
      })
    } else {
      const checkboxesToRemove = checkboxes[checkboxes.length - 1] // NOTE: Remove the last checkbox
      updateEntity({
        ...entity,
        subEntities: entity.subEntities.filter(
          subentity => subentity.id !== checkboxesToRemove.id,
        ),
      })
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {formAddNewSubentitySettingsList.map((slug, index) => (
        <>
          {HORIZONTAL_LINE_INDEXES.includes(index) && <HorizontalLine />}
          {
            <FormAddNewSubentitySettingsItem
              key={slug}
              title={getContactSlugName(slug, t)}
              icon={getFormSubentitiesSettingsIcons(slug)}
              controlComponent={() => (
                <Toggle
                  onChange={() => onToggle(slug)}
                  checked={getIsExisted(slug)}
                />
              )}
            />
          }
        </>
      ))}
      <HorizontalLine />
      <FormAddNewSubentitySettingsItem
        title={t('entity_settings.checkbox.header')}
        icon={CheckSquareIcon}
        controlComponent={() => (
          <FormSubentityNumberInput
            value={checkboxes.length}
            onChange={onChangeNumberOfCheckboxes}
          />
        )}
      />
    </div>
  )
}
